import React, {
  memo,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Button } from '@saleshandy/design-system';
import Select from '../../../../../../../../shared/design-system/components/select';
import Modal from '../../../../../../../../shared/design-system/ui/modal/modal';
import type { IProps } from './types';
import { getIsRequestPending } from '../../../../../../../../shared/utils';
import { UserPlus } from '../../../../../../../../shared/svg/user-plus';

const AssignPhoneNumberModal: React.FC<IProps> = ({
  show,
  onClose,
  onSubmit,
  teamMembersList = null,
  getTeamMembersListRequest,
  assignPhoneNumberRequestStatus,
  bulkAssignPhoneNumberRequestStatus,
}) => {
  const [selectedTeamMemberId, setSelectedTeamMemberId] = useState<
    number | null
  >(null);

  // Fetch team members when component mounts
  useEffect(() => {
    getTeamMembersListRequest?.();
  }, [getTeamMembersListRequest]);

  // Reset selected value when modal closes
  useEffect(() => {
    if (!show) {
      setSelectedTeamMemberId(null);
    }
  }, [show]);

  // Map team members to options for Select component
  const teamMemberOptions = useMemo(() => {
    if (!teamMembersList || teamMembersList.length === 0) {
      return [];
    }
    return teamMembersList.map((member) => ({
      key: String(member?.id),
      value: member?.name || member?.email || '',
    }));
  }, [teamMembersList]);

  const handleConfirm = () => {
    if (selectedTeamMemberId) {
      onSubmit?.(selectedTeamMemberId);
      // Don't close modal here - it will close on success in the parent component
    }
  };

  const handleCancel = () => {
    setSelectedTeamMemberId(null);
    onClose();
  };

  const isLoading =
    getIsRequestPending(assignPhoneNumberRequestStatus) ||
    getIsRequestPending(bulkAssignPhoneNumberRequestStatus);

  return (
    <Modal
      show={show}
      onHide={handleCancel}
      className="assign-phone-number-modal"
      backdrop="static"
      hideHeader={true}
      title={
        <div className="assign-phone-number-modal__header-content">
          <UserPlus className="assign-phone-number-modal__header-icon" />
          <span
            id="assign-phone-number-modal-title"
            className="assign-phone-number-modal__title"
          >
            Assign Phone Number(s)
          </span>
        </div>
      }
      hideFooter={false}
      footer={
        <div className="assign-phone-number-modal__footer-actions">
          <Button
            onClick={handleCancel}
            variant="secondary"
            className="assign-phone-number-modal__cancel-btn"
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleConfirm}
            className="assign-phone-number-modal__confirm-btn"
            disabled={!selectedTeamMemberId || isLoading}
            isLoading={isLoading}
            loadingText="Confirm"
          >
            Confirm
          </Button>
        </div>
      }
    >
      <div className="assign-phone-number-modal__body">
        <p className="assign-phone-number-modal__description">
          Assign the selected phone numbers to a single team member for calling
          and receiving purposes.
        </p>
        <div className="assign-phone-number-modal__select-container">
          <span className="assign-phone-number-modal__label">
            Select Team Member
          </span>
          <Select<{ key: string; value: string }>
            className="assign-phone-number-modal__select"
            options={teamMemberOptions}
            selectedOptionKey={
              selectedTeamMemberId ? String(selectedTeamMemberId) : null
            }
            selectedOptionRenderer={([option]) => (
              <span>{option?.value || 'Select Team Member'}</span>
            )}
            optionRenderer={(option) => <span>{option?.value}</span>}
            onChange={([option]) => {
              if (option) {
                setSelectedTeamMemberId(Number(option?.key));
              }
            }}
            placeholder="Search or Select Team Member"
            showSearch
            searchPlaceholder="Search team member"
            optionFilterProp="value"
            autoFocusSearch={true}
          />
        </div>
      </div>
    </Modal>
  );
};

export default memo(AssignPhoneNumberModal);
